import { Modal } from '../../../../../customComponents/Modal'
import {
  Alert,
  Box,
  FormControl,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  TextField,
  Typography
} from '@mui/material'
import type React from 'react'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { AttachMoney } from '@mui/icons-material'
import { ActivityEditSession } from '../../ActivityEditSession'
import { PriorityOneActivityName } from '../../../../../../models/PriorityOneActivityName'
import { type ProfessionalDevelopmentResource } from '../../../../../../models/ProfessionalDevelopmentResource'
import { AddProfessionalDevelopmentResource, UpdateProfessionalDevelopmentResource } from '../../../../../../services/ProfessionalDevelopmentResourceService'
import { useConfirmState } from '../../../../../../hooks/use-budgetThresholdExceeded'
import { BudgetThresholdExceededModal } from '../../../BudgetExceededModal'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'

export interface AddResourceFormValues {
  priorityId: number
  resourceNeeded: string
  resourcePurpose: string
  budgetRequest: string
}

export interface UpdateResourceFormValues extends AddResourceFormValues {
  id: number
}

interface Props {
  onClose: () => void
  onSaved: () => void
  initialValues?: ProfessionalDevelopmentResource
  priorityId: number
  implementationPhase?: boolean
}

export const ProfessionalDevelopmentResourceModal = (props: Props): JSX.Element => {
  const [formValues, setFormValues] = useState<AddResourceFormValues>({
    priorityId: props.priorityId ?? 0,
    resourceNeeded: '',
    resourcePurpose: '',
    budgetRequest: ''
  })
  const [submitted, setIsSubmitted] = useState(false)
  const { overBudgetThreshold, confirmState, setConfirmState } = useConfirmState(Number(props.initialValues?.budgetRequest), Number(formValues.budgetRequest))

  useEffect(() => {
    if (confirmState === 'confirmed') {
      void handleSubmit()
    }
  }, [confirmState])

  useEffect(() => {
    if (props.initialValues != null) {
      const initialFormValues: ProfessionalDevelopmentResource = props.initialValues
      initialFormValues.budgetRequest = initialFormValues.adjustedAmount?.toString() ?? initialFormValues.budgetRequest?.toString() ?? ''
      setFormValues(initialFormValues)
    }
  }, [props.initialValues])

  const hasInvalidFormValues = formValues.resourceNeeded === '' ||
        formValues.resourcePurpose === '' ||
        formValues.budgetRequest === ''

  const handleFieldUpdate = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target
    setFormValues({ ...formValues, [name]: value })
  }

  const handleSubmit = async (): Promise<void> => {
    setIsSubmitted(true)
    if (hasInvalidFormValues) {
      toast.error('Please fill out all required fields')
      return
    }
    if (overBudgetThreshold && confirmState !== 'confirmed') {
      setConfirmState('needsConfirmation')
      return
    }

    const { success, errors } = props.initialValues != null
      ? await UpdateProfessionalDevelopmentResource({
        ...formValues,
        id: props.initialValues.id,
        needsReview: overBudgetThreshold,
        isUpdate: props.initialValues.isUpdate,
        implementationPhase: props.implementationPhase ?? false
      })
      : await AddProfessionalDevelopmentResource({
        ...formValues,
        implementationPhase: props.implementationPhase ?? false
      })

    if (!success) {
      errors.forEach((error) => toast.error(error))
      return
    }

    toast.success(`Resource ${props.initialValues != null ? 'Updated' : 'Saved'}`)

    props.onClose()
    props.onSaved()
  }

  return (confirmState === 'needsConfirmation'
    ? <BudgetThresholdExceededModal
        onClose={props.onClose}
        onConfirm={async () => {
          setConfirmState('confirmed')
        }}
    />
    : <Modal
        title='Additional Resource'
        open={true}
        data-testid={props?.initialValues != null ? 'editResourceModal' : 'addResourceModal'}
        width='480px'
        confirmButtonText='Save'
        cancelButtonText='Cancel'
        onClose={props.onClose}
        onConfirm={handleSubmit}
        bodyContent={
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <Typography variant='h5'>Resource Info</Typography>
            {
              props.implementationPhase === true && props.initialValues == null &&
                <Alert data-testid='contributorAlert' icon={<InfoOutlinedIcon fontSize="inherit" sx={{ fill: 'white' }} />} sx={{
                  backgroundColor: 'var(--blue-500)',
                  color: 'white',
                  fill: 'white'
                }} severity="info"
                >The addition of this item will prompt a review from the START team.
                </Alert>
            }
            <TextField
              name='resourceNeeded'
              label='Resource Needed'
              value={formValues.resourceNeeded}
              data-testid='resourceNeeded'
              onChange={handleFieldUpdate}
              variant='filled'
              multiline={true}
              rows={4}
              required={formValues.resourceNeeded === ''}
              error={submitted && formValues.resourceNeeded === ''}
            />

            <TextField
              name='resourcePurpose'
              label='Resource Purpose'
              value={formValues.resourcePurpose}
              data-testid='resourcePurpose'
              onChange={handleFieldUpdate}
              variant='filled'
              multiline={true}
              rows={4}
              required={formValues.resourcePurpose === ''}
              error={submitted && formValues.resourcePurpose === ''}
            />

            <FormControl sx={{ width: 'fit-content' }} variant="outlined">
              <InputLabel htmlFor="budget-request">Budget Request</InputLabel>
              <OutlinedInput
                id="budget-request"
                startAdornment={
                  <InputAdornment position="start">
                    <AttachMoney />
                  </InputAdornment>
            }
                name='budgetRequest'
                label="Budget Request"
                value={formValues.budgetRequest}
                data-testid='budgetRequest'
                onChange={handleFieldUpdate}
                required={formValues.budgetRequest === ''}
                error={submitted && formValues.budgetRequest === ''}
              />
            </FormControl>

            {props.initialValues != null &&
              <ActivityEditSession
                activityId={props.initialValues.id}
                activityName={PriorityOneActivityName.OtherProfessionalDevelopmentResources}
                onClose={props.onClose}
              />
        }
          </Box>
    }
    />)
}
